/**
 * Single source of truth for the app sidebar navigation.
 *
 * Adding a new top-level destination means appending an entry here —
 * the sidebar component renders whatever this exports. Sections are
 * rendered in order; a `null` section label hides the group heading
 * for the first (default) group.
 *
 * Icons are Phosphor glyphs rendered through the shared `<Icon>` wrapper
 * (`components/ui/icon`), so the whole app draws from one icon set at one
 * default size/weight. They stay decorative (`aria-hidden`, handled by the
 * wrapper) because the label text is the accessible name.
 */
import {
  ChartLineUp,
  Copy,
  Eye,
  House,
  PaperPlaneTilt,
  Robot,
  SquaresFour,
  UsersThree,
  VideoCamera,
} from '@phosphor-icons/react';

import { Icon } from '@/components/ui/icon/icon';
import { paths } from '@/config/paths';

import type { ReactNode } from 'react';

export type NavKey =
  | 'dashboard'
  | 'avatars'
  | 'videos'
  | 'generate'
  | 'copy-reel'
  | 'followed'
  | 'reels-analytics'
  | 'posts'
  | 'automation'
  | 'campaign';

export interface NavItem {
  key: NavKey;
  label: string;
  to: string;
  icon: ReactNode;
  /** True for features deferred to post-launch · rendered as a non-clickable
   *  span with a small "Soon" pill. Source of truth for which features are
   *  deferred lives in D-domains/06-avatars-automation/memory/roadmap.md. */
  soon?: boolean;
  /** True when `to` is an absolute URL outside the SPA (e.g. a server-rendered
   *  Flask page). Rendered as a regular `<a href>` instead of React-Router
   *  `<Link>`. No nav item currently sets this — kept for the rare external link. */
  external?: boolean;
}

export interface NavSection {
  label: string | null;
  items: NavItem[];
}

/**
 * SOON nav items (Posts, Automation, Campaign Studio) are hidden behind this
 * flag (Lior, 2026-06-29). Flip to `true` to restore all three instantly.
 * Their item definitions and icon imports are kept intact below, so nothing
 * needs to be rebuilt. The icons each one uses, for the record:
 *   - Posts           → PaperPlaneTilt
 *   - Automation      → Robot
 *   - Campaign Studio → SquaresFour
 */
export const SHOW_SOON_NAV = false;

const RAW_NAV_SECTIONS: readonly NavSection[] = [
  {
    label: null,
    items: [
      {
        key: 'dashboard',
        label: 'Home',
        to: paths.dashboard,
        icon: <Icon icon={House} />,
      },
      {
        key: 'avatars',
        label: 'Avatars',
        to: paths.avatars,
        icon: <Icon icon={UsersThree} />,
      },
      {
        key: 'videos',
        label: 'Videos',
        to: paths.videos,
        icon: <Icon icon={VideoCamera} />,
      },
      // 'Generate Video' nav entry removed from the menu (Lior, 2026-06-29):
      // the Generate Video page (paths.generate) is now reached from the Videos
      // page "+ New video" button + empty state and the dashboard CTA, so it
      // does not need its own sidebar item. For the record, it used the Sparkle
      // icon; re-add an item with `to: paths.generate` here to restore it.
      {
        key: 'copy-reel',
        label: 'Copy From Reel',
        to: paths.copyReel,
        icon: <Icon icon={Copy} />,
      },
    ],
  },
  {
    label: 'Viral Radar',
    items: [
      {
        key: 'followed',
        label: 'Followed Accounts',
        to: paths.followed,
        icon: <Icon icon={Eye} />,
      },
      {
        // The cross-account reel browser (server-paginated table). The
        // old Flask-rendered analytics page and the "soon" general
        // dashboard have both been retired in favour of this SPA route.
        key: 'reels-analytics',
        label: 'Reel Analytics',
        to: paths.reelAnalytics,
        icon: <Icon icon={ChartLineUp} />,
      },
      {
        key: 'posts',
        label: 'Posts',
        to: paths.posts,
        soon: true,
        icon: <Icon icon={PaperPlaneTilt} />,
      },
      {
        key: 'automation',
        label: 'Automation',
        to: paths.automation,
        soon: true,
        icon: <Icon icon={Robot} />,
      },
      {
        key: 'campaign',
        label: 'Campaign Studio',
        to: paths.campaign,
        soon: true,
        icon: <Icon icon={SquaresFour} />,
      },
      // Ideation Studio · cut from scope 2026-05-17 (redundant with the
      // trending-ideas surface inside the Video Wizard's script step).
      // See D-domains/06-avatars-automation/memory/roadmap.md → Cut.
    ],
  },
];

/**
 * What the sidebar renders. Identical to `RAW_NAV_SECTIONS` except the SOON
 * items are filtered out while `SHOW_SOON_NAV` is false. Because the raw list
 * still references each SOON item (and its icon), the imports stay live and
 * restoring them is a one-line flag flip.
 */
export const NAV_SECTIONS: readonly NavSection[] = RAW_NAV_SECTIONS.map((section) => ({
  ...section,
  items: SHOW_SOON_NAV ? section.items : section.items.filter((item) => !item.soon),
}));
